import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Divider,
  makeStyles,
} from "@material-ui/core";
import { ICamperVanDetails } from "../../../../reducers/bookings/types";
import { getFeatureTypeIcon } from "../../../utils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: 20,
    fontWeight: 600,
  },
  titleIcon: {
    height: 35,
    width: 35,
    marginRight: theme.spacing(1),
    cursor: "pointer",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    "& > div": {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  chipBox: {
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: "0.85rem",
    fontWeight: 500,
    background: "linear-gradient(135deg, #fafafa, #ffffff)",
    border: "1px solid #eee",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  bedBox: {
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    background: "linear-gradient(135deg, #f0f4ff, #ffffff)",
    border: "1px solid #e0e6f5",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    minWidth: 140,
    marginRight: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  bedTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  capacityBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 20,
    background: "linear-gradient(135deg, #fafafa, #ffffff)",
    border: "1px solid #eee",
    marginBottom: theme.spacing(1.5),
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    fontWeight: 500,
    fontSize:"0.85rem"
  },
}));

interface IProps {
  open: boolean;
  onClose: () => void;
  camperVanDetails?: ICamperVanDetails;
}

const CamperVanDetailModal: React.FC<IProps> = ({
  open,
  onClose,
  camperVanDetails,
}) => {
  const classes = useStyles();

  if (!camperVanDetails) return null;

  const renderChipList = (items: string[]) => (
    <div className={classes.chipContainer}>
      {items.map((item, idx) => (
        <div key={idx} className={classes.chipBox}>
          {item}
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <img
          src={getFeatureTypeIcon("camperVan")}
          alt="camperVanDetails"
          className={classes.titleIcon}
        />
        Camper Van Specifications
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Sleeping Capacity */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Sleeping Capacity
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {camperVanDetails.sleepingCapacity.totalSleepingCapacity > 0 && (
                <div className={classes.capacityBadge}>
                  <Typography variant="body1">
                    Total Capacity: {camperVanDetails.sleepingCapacity.totalSleepingCapacity} people
                  </Typography>
                </div>
              )}
              <div className={classes.capacityBadge}>
                <Typography variant="body1">
                  No. of beds:{" "}{camperVanDetails.sleepingCapacity.numberOfBeds}
                </Typography>
              </div>
            </div>
            {/* Bed Details Section */}
            {camperVanDetails.sleepingCapacity.beds.length > 0 && (
            <Typography variant="h6" style={{ 
              marginTop: 2, 
              marginBottom: 12, 
              fontWeight: 600, 
              color: "#424242",
            }}>
              Bed Specifications
            </Typography>
            )}
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {camperVanDetails.sleepingCapacity.beds.map(
                (bed: any, i: number) => (
                  <div key={i} className={classes.bedBox}>
                    <Typography variant="subtitle2" className={classes.bedTitle}>
                      {bed.bedType}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {bed.bedDimension.length} × {bed.bedDimension.breadth} cm
                    </Typography>
                  </div>
                )
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Kitchen */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Kitchen
            </Typography>
            {renderChipList(camperVanDetails.kitchen)}
          </Grid>

          {/* Utilities */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Utilities
            </Typography>
            {renderChipList(camperVanDetails.utilities)}
          </Grid>

          {/* Bathroom */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Bathroom
            </Typography>
            {renderChipList(camperVanDetails.bathroom)}
          </Grid>

          {/* Driving */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Driving
            </Typography>
            {renderChipList(camperVanDetails.driving)}
          </Grid>

          {/* Others */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Others
            </Typography>
            {renderChipList(camperVanDetails.others)}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CamperVanDetailModal;
